import * as Sentry from "@sentry/react";

import type {Me} from "@/src/modules/core/types";

import {beforeSendEvent} from "./before-send";
import {isGlitchTipEnabled} from "./capture";
import {captureUnhandledRejection} from "./device-capture";
import {getUserRoleSlug} from "./user-role-slug";

const GLITCHTIP_DSN = import.meta.env.VITE_GLITCHTIP_DSN;
const IS_DEV_BUILD = import.meta.env.MODE === "development";

function getGlitchTipRelease(): string {
  const version = import.meta.env.VITE_APP_VERSION;
  if (typeof version === "string" && version.length > 0) return version;
  return APP_BUILD_ID;
}

function shouldDropVersionJsonBreadcrumb(url: unknown): boolean {
  return typeof url === "string" && url.includes("/version.json");
}

export function initGlitchTip(): void {
  if (!GLITCHTIP_DSN) return;

  Sentry.init({
    dsn: GLITCHTIP_DSN,
    environment: import.meta.env.VITE_GLITCHTIP_ENVIRONMENT ?? "production",
    release: getGlitchTipRelease(),
    sendClientReports: false,
    debug: IS_DEV_BUILD,
    maxBreadcrumbs: 15,
    beforeBreadcrumb(breadcrumb) {
      if (
        breadcrumb.category === "xhr" &&
        shouldDropVersionJsonBreadcrumb(breadcrumb.data?.url)
      ) {
        return null;
      }
      return breadcrumb;
    },
    initialScope: {
      tags: {
        instance_slug: import.meta.env.VITE_INSTANCE_SLUG,
        surface: "web",
      },
    },
    beforeSend: beforeSendEvent,
  });

  if (typeof window !== "undefined") {
    window.addEventListener("unhandledrejection", (event) => {
      captureUnhandledRejection(event.reason);
    });
  }
}

export function setGlitchTipRoute(route: string): void {
  if (!isGlitchTipEnabled()) return;

  const normalized = route || "/";
  Sentry.setTag("route", normalized);
  Sentry.getCurrentScope().setTransactionName(normalized);
}

export function setGlitchTipUser(me: Me): void {
  if (!isGlitchTipEnabled()) return;

  Sentry.setUser({id: String(me.user_id)});
  Sentry.setTag("user_role", getUserRoleSlug(me.role));
}

export function clearGlitchTipUser(): void {
  if (!isGlitchTipEnabled()) return;

  Sentry.setUser(null);
  Sentry.setTag("user_role", undefined);
}

export {isGlitchTipEnabled};
